import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import NOVOLoader from '../../shared/components/NOVOLoader';
import { getFallbackRedirect, getRedirectForAction } from './actionHandlers';
import usePersistDeeplinkData from './hooks/usePersistDeeplinkData';
import usePublicApplicationTypes from './hooks/usePublicApplicationTypes';
import useRouteFallbackRedirect from './hooks/useRouteFallbackRedirect';
import useRouteLanguage from './hooks/useRouteLanguage';
import useRouteProfileSync from './hooks/useRouteProfileSync';
import useRouteQueryParams from './hooks/useRouteQueryParams';
import {
  buildApplicationsPath,
  buildPublicFormApplicationsPath,
  isApplicationsAction,
} from './routeUtils';

const RouteHandler = ({ location, setHeaderTitle }) => {
  const isAuthenticated = useSelector(
    (state) => !!state.authReducer.auth_token
  );
  const selectedLanguage = useSelector(
    (state) => state.settingsReducer?.selectedLanguage
  );

  const { deeplinkKey, hasDeeplinkParams, queryParams } = useRouteQueryParams(
    location.search
  );
  const appLanguage = useRouteLanguage({
    queryLang: queryParams.lang,
    selectedLanguage,
  });

  const shouldWaitForDeeplinkPersistence = usePersistDeeplinkData({
    deeplinkKey,
    hasDeeplinkParams,
    queryParams,
  });
  const shouldWaitForProfileSync = useRouteProfileSync({
    queryParams,
    setHeaderTitle,
  });
  const { publicApplicationTypeFound, shouldWaitForApplicationTypes } =
    usePublicApplicationTypes({
      appLanguage,
      queryParams,
    });

  const redirectComponent = useMemo(
    () => getRedirectForAction({ ...queryParams, isAuthenticated }),
    [queryParams, isAuthenticated]
  );
  const fallbackRedirect = useRouteFallbackRedirect({
    action: queryParams.action,
    redirectComponent,
  });
  const shouldRedirectToPrivateApplications =
    isAuthenticated &&
    isApplicationsAction(queryParams.action) &&
    queryParams.authority &&
    publicApplicationTypeFound === false;
  const shouldRedirectToPublicFormApplications =
    !isAuthenticated &&
    isApplicationsAction(queryParams.action) &&
    queryParams.authority &&
    publicApplicationTypeFound === false;

  if (
    shouldRedirectToPrivateApplications &&
    !shouldWaitForDeeplinkPersistence &&
    !shouldWaitForApplicationTypes
  ) {
    return <Redirect to={buildApplicationsPath(queryParams)} />;
  }

  if (
    shouldRedirectToPublicFormApplications &&
    !shouldWaitForDeeplinkPersistence &&
    !shouldWaitForApplicationTypes
  ) {
    return <Redirect to={buildPublicFormApplicationsPath(queryParams)} />;
  }

  if (
    redirectComponent &&
    !shouldWaitForProfileSync &&
    !shouldWaitForDeeplinkPersistence &&
    !shouldWaitForApplicationTypes
  ) {
    return redirectComponent;
  }

  if (fallbackRedirect) {
    return getFallbackRedirect();
  }

  return (
    <div className="flex flex-1 items-center justify-center">
      <NOVOLoader />
    </div>
  );
};

export default withRouter(RouteHandler);
